import { useQuery } from '@tanstack/react-query';

import * as adminApi from '@/apis/admin';
import type {
  GetAdminNoticeAudiencesResponse,
  GetAdminNoticeResponse,
  GetAdminNoticesMainResponse,
  GetAdminNoticesMainParams,
  GetAdminProductOrderResponse,
  GetAdminProductOrdersParams,
  GetAdminProductOrdersResponse,
  GetAdminProductResponse,
  GetAdminProductsParams,
  GetAdminProductsResponse,
  GetAllBranchesResponse,
  GetOrderNoteLogsParams,
  GetOrderNoteLogsResponse,
  GetOrderStatusLogsParams,
  GetOrderStatusLogsResponse,
} from '@/types/admin';
import type { PathVariableRequestParams } from '@/types/api';
import type { CommonUseQueryOptions } from '@/types/app';
import { getQueryKey } from '@/utils/queryKeys';

/** Notice */
export const useAdminNoticesMain = (
  params: GetAdminNoticesMainParams,
  options?: CommonUseQueryOptions<GetAdminNoticesMainResponse>
) => {
  const query = useQuery({
    queryKey: getQueryKey('admin:getNoticesMain', params),
    queryFn: () => adminApi.getAdminNoticesMain(params),
    ...options,
  });

  return query;
};

export const useAdminNotice = (
  params: PathVariableRequestParams,
  options?: CommonUseQueryOptions<GetAdminNoticeResponse>
) => {
  const query = useQuery({
    queryKey: getQueryKey('admin:getNotice', params),
    queryFn: () => adminApi.getAdminNotice(params),
    ...options,
  });

  return query;
};

export const useAdminNoticeAudiences = (
  params: PathVariableRequestParams,
  options?: CommonUseQueryOptions<GetAdminNoticeAudiencesResponse>
) => {
  const query = useQuery({
    queryKey: getQueryKey('admin:getNoticeAudiences', params),
    queryFn: () => adminApi.getAdminNoticeAudiences(params),
    ...options,
  });

  return query;
};

export const useAllBranches = (
  options?: CommonUseQueryOptions<GetAllBranchesResponse>
) => {
  const query = useQuery({
    queryKey: getQueryKey('admin:getAllBranches'),
    queryFn: () => adminApi.getAllBranches(),
    ...options,
  });

  return query;
};

export const useAdminProducts = (
  params: GetAdminProductsParams,
  options?: CommonUseQueryOptions<GetAdminProductsResponse>
) => {
  const query = useQuery({
    queryKey: getQueryKey('admin:getProducts', params),
    queryFn: () => adminApi.getAdminProducts(params),
    ...options,
  });

  return query;
};

export const useAdminProduct = (
  params: PathVariableRequestParams,
  options?: CommonUseQueryOptions<GetAdminProductResponse>
) => {
  const query = useQuery({
    queryKey: getQueryKey('admin:getProduct', params),
    queryFn: () => adminApi.getAdminProduct(params),
    ...options,
  });

  return query;
};

export const useAdminProductOrders = (
  params: GetAdminProductOrdersParams,
  options?: CommonUseQueryOptions<GetAdminProductOrdersResponse>
) => {
  const query = useQuery({
    queryKey: getQueryKey('admin:getProductOrders', params),
    queryFn: () => adminApi.getAdminProductOrders(params),
    ...options,
  });

  return query;
};

export const useAdminProductOrder = (
  params: PathVariableRequestParams,
  options: CommonUseQueryOptions<GetAdminProductOrderResponse>
) => {
  const query = useQuery({
    queryKey: getQueryKey('admin:getProductOrder', params),
    queryFn: () => adminApi.getAdminProductOrder(params),
    ...options,
  });

  return query;
};

export const useOrderStatusLogs = (
  params: GetOrderStatusLogsParams,
  options: CommonUseQueryOptions<GetOrderStatusLogsResponse>
) => {
  const query = useQuery({
    queryKey: getQueryKey('admin:getOrderStatusLogs', params),
    queryFn: () => adminApi.getOrderStatusLogs(params),
    ...options,
  });

  return query;
};

export const useOrderNoteLogs = (
  params: GetOrderNoteLogsParams,
  options: CommonUseQueryOptions<GetOrderNoteLogsResponse>
) => {
  const query = useQuery({
    queryKey: getQueryKey('admin:getOrderNoteLogs', params),
    queryFn: () => adminApi.getOrderNoteLogs(params),
    ...options,
  });

  return query;
};
